import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { Checkbox, CheckboxField, CheckboxGroup } from "src/components/base/checkbox";
import Form from "src/components/base/form";
import { FullSplitDnsDomain } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link UpdateDomainDialog}
 */
export type UpdateDomainDialogProps = {
    /** Split dns domain to be updated */
    domain: FullSplitDnsDomain | undefined;
    /** Whether the dialog is open or not */
    isOpen: boolean;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for submitting the form */
    onSubmit: () => void;
};

/**
 * A dialog for updating a split dns domain
 */
export default function UpdateDomainDialog(props: UpdateDomainDialogProps) {
    const [t] = useTranslation("dialog-add-domain");

    const form = useForm({
        defaultValues: {
            domainName: props.domain?.domain ?? "",
            disabled: props.domain?.disabled ?? false,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.domain) return;
                const res = await ApiInternal.dns.updateDomainSplitDns(props.domain.uuid, {
                    domain: value.domainName,
                    disabled: value.disabled,
                });

                if (res.result === "Err") {
                    return {
                        fields: {
                            domainName: res.error.domain_invalid
                                ? t("error.invalid-domain")
                                : res.error.domain_already_exists
                                  ? t("error.domain-already-exists")
                                  : undefined,
                        },
                    };
                }

                props.onSubmit();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.domain]);

    if (!props.domain) return null;

    return (
        <Dialog onClose={props.onClose} open={props.isOpen}>
            <Form onSubmit={form.handleSubmit}>
                <DialogTitle>{t("heading.edit-domain")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"domainName"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            maxLength={255}
                                            required={true}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage key={err}>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <form.Field name={"disabled"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.disabled")}</Label>
                                        <CheckboxGroup>
                                            <CheckboxField>
                                                <Checkbox
                                                    checked={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e)}
                                                />
                                                <Label>{t("description.disabled")}</Label>
                                            </CheckboxField>
                                        </CheckboxGroup>
                                    </Field>
                                )}
                            </form.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <PrimaryButton type={"submit"}>{t("button.update")}</PrimaryButton>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
